import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  X, Loader2, Save, User, Phone, MapPin, Home, Languages, Wallet,
  Droplet, HeartPulse, ShieldAlert, LifeBuoy, LogOut, CheckCircle2, ChevronDown,
} from "lucide-react";
import {
  isAuthed, setAuth, getRememberedPhone, setRememberedPhone, clearRememberedPhone,
} from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "elderra_session_id";

const LANGUAGES = [
  { value: "en", label: "English" },
  { value: "hi", label: "हिंदी (Hindi)" },
  { value: "bn", label: "বাংলা (Bengali)" },
  { value: "ta", label: "தமிழ் (Tamil)" },
  { value: "te", label: "తెలుగు (Telugu)" },
  { value: "mr", label: "मराठी (Marathi)" },
  { value: "gu", label: "ગુજરાતી (Gujarati)" },
  { value: "kn", label: "ಕನ್ನಡ (Kannada)" },
  { value: "ml", label: "മലയാളം (Malayalam)" },
  { value: "pa", label: "ਪੰਜਾਬੀ (Punjabi)" },
];

const BLOOD_GROUPS = ["", "A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

const EMPTY = {
  name: "", phone: "", city: "", address: "", language: "en", payment_mode: "cash",
  blood_group: "", allergies: "", conditions: "",
  emergency_contact_name: "", emergency_contact_phone: "",
};

const SECTION_STYLES = {
  personal: { bg: "bg-[#EEF2FF]", border: "border-[#C7D2FE]", icon: "text-[#2D2766]" },
  address: { bg: "bg-[#F0FDF4]", border: "border-[#BBF7D0]", icon: "text-[#166534]" },
  health: { bg: "bg-[#FFF1F2]", border: "border-[#FECDD3]", icon: "text-[#9F1239]" },
  emergency: { bg: "bg-[#FFFBEB]", border: "border-[#FDE68A]", icon: "text-[#92400E]" },
};

function ProfileSection({ accent, icon: Icon, title, subtitle, children, testid }) {
  const s = SECTION_STYLES[accent];
  return (
    <div className={`rounded-2xl border ${s.border} ${s.bg} p-4 sm:p-5`} data-testid={testid}>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shadow-sm shrink-0">
          <Icon className={`w-5 h-5 ${s.icon}`} />
        </div>
        <div>
          <h3 className="text-base sm:text-lg font-bold text-[#1A1A1A] leading-tight">{title}</h3>
          <p className="text-xs sm:text-sm text-slate-600">{subtitle}</p>
        </div>
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

export default function MyProfileModal({ sessionId, onClose, onSaved }) {
  const isLoggedIn = isAuthed();
  const [form, setForm] = useState(EMPTY);
  const [hasProfile, setHasProfile] = useState(false);
  const [optOut, setOptOut] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const fetchStarted = useRef(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const inputCls = "w-full min-h-[56px] px-4 text-base sm:text-lg border-2 border-slate-300 rounded-2xl focus:outline-none focus:ring-4 focus:ring-[#2D2766]/20 focus:border-[#2D2766] bg-white text-[#1A1A1A] transition-all placeholder:text-slate-400";
  const selectCls = `${inputCls} appearance-none pr-10 cursor-pointer`;
  const labelCls = "flex items-center gap-2 text-sm sm:text-base font-semibold text-[#1A1A1A] mb-1.5";

  const phoneLocked = isLoggedIn || hasProfile;
  const initials = form.name.trim()
    ? form.name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase()
    : null;

  const normalizeProfile = (p) => {
    const j = (v) => (Array.isArray(v) ? v.join(", ") : v ?? "");
    return {
      ...EMPTY,
      ...p,
      phone: (p.phone || "").replace(/^91/, ""),
      allergies: j(p.allergies),
      conditions: j(p.conditions),
    };
  };

  useEffect(() => {
    // Guards against React StrictMode's double-invoked mount effect — without
    // this, the second (delayed) fetch can resolve after the user has already
    // started typing and silently wipe out their in-progress phone entry.
    if (fetchStarted.current) return;
    fetchStarted.current = true;
    (async () => {
      try {
        if (isLoggedIn) {
          const { data } = await axios.get(`${API}/user/me`);
          if (data.profile) { setForm(normalizeProfile(data.profile)); setHasProfile(true); setOptOut(!!data.profile.remember_opt_out); }
        } else if (sessionId) {
          const { data } = await axios.get(`${API}/user/profile/session/${sessionId}`);
          setOptOut(!!data.remember_opt_out);
          if (data.profile) {
            setForm(normalizeProfile(data.profile));
            setHasProfile(true);
            if (data.profile.phone) setRememberedPhone(data.profile.phone);
          } else {
            setForm((f) => ({ ...f, phone: (getRememberedPhone() || "").replace(/^91/, "") }));
          }
        }
      } catch (_) { /* new/empty profile */ }
      finally { setLoading(false); }
    })();
  }, [isLoggedIn, sessionId]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!isLoggedIn && !hasProfile && !/^\d{10}$/.test((form.phone || "").replace(/\D/g, "").slice(-10))) {
      toast.error("Please enter your 10-digit phone number so we can save your profile.");
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name, city: form.city, address: form.address,
      language: form.language, payment_mode: form.payment_mode,
      blood_group: form.blood_group, allergies: form.allergies, conditions: form.conditions,
      emergency_contact_name: form.emergency_contact_name,
      emergency_contact_phone: form.emergency_contact_phone,
    };
    try {
      if (isLoggedIn) {
        const { data } = await axios.patch(`${API}/user/profile`, payload);
        setAuth(data.profile);
        onSaved?.(data.profile);
      } else {
        const { data } = await axios.patch(`${API}/user/profile/session`, { session_id: sessionId, phone: form.phone, ...payload });
        if (data.profile?.phone) setRememberedPhone(data.profile.phone);
        onSaved?.(data.profile);
      }
      toast.success("Your details are saved.");
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleRemember = async () => {
    const next = !optOut; // next = new opt-out value
    try {
      if (isLoggedIn) {
        await axios.patch(`${API}/user/profile`, { remember_opt_out: next });
      } else {
        await axios.post(`${API}/user/profile/session/opt-out`, { session_id: sessionId, opt_out: next });
      }
      setOptOut(next);
      toast.success(next ? "We'll stop saving your details." : "We'll remember your details again.");
    } catch (_) {
      toast.error("Could not update this setting.");
    }
  };

  const handleForget = () => {
    clearRememberedPhone();
    localStorage.removeItem(SESSION_KEY);
    window.location.reload();
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end md:items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="my-profile-modal">
      <div className="absolute inset-0" onClick={onClose} />
      <div className="relative bg-white rounded-t-[28px] md:rounded-[28px] w-full md:max-w-2xl shadow-2xl max-h-[92vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/90 hover:bg-gray-100 shadow-sm transition-colors" aria-label="Close" data-testid="my-profile-close-btn">
          <X className="w-5 h-5 text-gray-500" />
        </button>

        <div className="px-6 md:px-8 pt-7 pb-5 flex items-center gap-4 border-b border-gray-100">
          <div className="w-16 h-16 shrink-0 rounded-full bg-[#2D2766] ring-4 ring-[#2D2766]/10 flex items-center justify-center shadow-lg">
            {initials ? (
              <span className="text-white text-xl font-bold">{initials}</span>
            ) : (
              <User className="w-7 h-7 text-white" />
            )}
          </div>
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#1A1A1A]" data-testid="my-profile-title">My Profile</h2>
            <p className="text-sm sm:text-base text-gray-500 mt-0.5">
              Elderra saves these so you never have to type them again.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="py-16 flex justify-center" data-testid="my-profile-loading">
            <Loader2 className="w-7 h-7 animate-spin text-[#2D2766]" />
          </div>
        ) : (
          <form onSubmit={handleSave} className="px-6 md:px-8 py-6 space-y-5">
            <ProfileSection accent="personal" icon={User} title="Personal Information" subtitle="Your basic contact details" testid="my-profile-section-personal">
              <div>
                <label className={labelCls}><User className="w-4 h-4" /> Name</label>
                <input className={inputCls} value={form.name} onChange={set("name")} placeholder="Your name" data-testid="my-profile-name" />
              </div>
              <div>
                <label className={labelCls}><Phone className="w-4 h-4" /> Phone number</label>
                {phoneLocked ? (
                  <div className="relative">
                    <input className={`${inputCls} bg-slate-100 border-slate-200 text-slate-600 pr-28 cursor-not-allowed`} value={form.phone} disabled data-testid="my-profile-phone" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-full">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Verified
                    </span>
                  </div>
                ) : (
                  <input
                    className={inputCls}
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value.replace(/\D/g, "").slice(0, 12) }))}
                    placeholder="10-digit mobile number"
                    data-testid="my-profile-phone"
                  />
                )}
                {phoneLocked && <p className="text-xs sm:text-sm text-gray-400 mt-1">Your phone number can't be changed here.</p>}
              </div>
              <div>
                <label className={labelCls}><Languages className="w-4 h-4" /> Preferred language</label>
                <div className="relative">
                  <select className={selectCls} value={form.language} onChange={set("language")} data-testid="my-profile-language">
                    {LANGUAGES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
                  </select>
                  <ChevronDown className="w-5 h-5 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
              </div>
            </ProfileSection>

            <ProfileSection accent="address" icon={MapPin} title="Location & Address" subtitle="For home visits & deliveries" testid="my-profile-section-address">
              <div>
                <label className={labelCls}><MapPin className="w-4 h-4" /> City</label>
                <input className={inputCls} value={form.city} onChange={set("city")} placeholder="e.g. Mumbai" data-testid="my-profile-city" />
              </div>
              <div>
                <label className={labelCls}><Home className="w-4 h-4" /> Home address</label>
                <input className={inputCls} value={form.address} onChange={set("address")} placeholder="Flat/house no, street, area" data-testid="my-profile-address" />
              </div>
            </ProfileSection>

            <ProfileSection accent="health" icon={HeartPulse} title="Health & Safety Profile" subtitle="Essential medical information for SOS" testid="my-profile-section-health">
              <div>
                <label className={labelCls}><Droplet className="w-4 h-4" /> Blood group</label>
                <div className="relative">
                  <select className={selectCls} value={form.blood_group} onChange={set("blood_group")} data-testid="my-profile-blood-group">
                    {BLOOD_GROUPS.map((b) => <option key={b} value={b}>{b || "Not sure"}</option>)}
                  </select>
                  <ChevronDown className="w-5 h-5 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
              </div>
              <div>
                <label className={labelCls}><ShieldAlert className="w-4 h-4" /> Allergies</label>
                <input className={inputCls} value={form.allergies} onChange={set("allergies")} placeholder="e.g. penicillin, peanuts (or leave blank)" data-testid="my-profile-allergies" />
              </div>
              <div>
                <label className={labelCls}><HeartPulse className="w-4 h-4" /> Health conditions</label>
                <input className={inputCls} value={form.conditions} onChange={set("conditions")} placeholder="e.g. diabetes, blood pressure" data-testid="my-profile-conditions" />
              </div>
            </ProfileSection>

            <ProfileSection accent="emergency" icon={ShieldAlert} title="Emergency Contact & Payment" subtitle="Who to reach & preferred payment" testid="my-profile-section-emergency">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}><LifeBuoy className="w-4 h-4" /> Emergency name</label>
                  <input className={inputCls} value={form.emergency_contact_name} onChange={set("emergency_contact_name")} placeholder="e.g. Ramesh (son)" data-testid="my-profile-emergency-name" />
                </div>
                <div>
                  <label className={labelCls}><Phone className="w-4 h-4" /> Emergency phone</label>
                  <input
                    className={inputCls}
                    value={form.emergency_contact_phone}
                    onChange={(e) => setForm((f) => ({ ...f, emergency_contact_phone: e.target.value.replace(/\D/g, "").slice(0, 12) }))}
                    placeholder="10-digit number"
                    data-testid="my-profile-emergency-phone"
                  />
                </div>
              </div>
              <div>
                <label className={labelCls}><Wallet className="w-4 h-4" /> Payment mode</label>
                <div className="grid grid-cols-2 gap-3">
                  {["cash", "online"].map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, payment_mode: m }))}
                      className={`min-h-[52px] rounded-2xl border-2 font-semibold capitalize transition-all active:scale-[0.98] ${
                        form.payment_mode === m
                          ? "border-[#2D2766] bg-[#2D2766] text-white shadow-md"
                          : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                      }`}
                      data-testid={`my-profile-payment-${m}`}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              </div>
            </ProfileSection>

            <button
              type="submit"
              disabled={saving}
              className="w-full min-h-[60px] bg-[#2D2766] text-white rounded-2xl font-bold text-lg hover:bg-[#3B3382] active:scale-[0.98] shadow-lg shadow-[#2D2766]/20 transition-all disabled:opacity-60 flex items-center justify-center gap-2"
              data-testid="my-profile-save-btn"
            >
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
              Save my details
            </button>

            {/* Remember toggle + forget-on-device */}
            <div className="space-y-3">
              <label className="p-4 rounded-2xl bg-[#EEF2FF]/60 border border-[#C7D2FE] flex items-start gap-3 cursor-pointer" data-testid="my-profile-remember-toggle">
                <input type="checkbox" checked={!optOut} onChange={handleToggleRemember} className="mt-1 w-5 h-5 accent-[#2D2766]" />
                <span className="text-sm sm:text-base text-gray-700">
                  <span className="font-semibold text-[#1A1A1A]">Remember my details</span><br />
                  When on, Elderra saves what you share so you don't have to repeat it. Turn off to stop saving.
                </span>
              </label>
              {!isLoggedIn && (
                <button
                  type="button"
                  onClick={handleForget}
                  className="w-full min-h-[48px] rounded-2xl border-2 border-slate-300 text-slate-700 font-semibold text-sm sm:text-base hover:bg-slate-100 flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
                  data-testid="my-profile-forget-btn"
                >
                  <LogOut className="w-4 h-4" /> Not you? Forget me on this device
                </button>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
